// Copyright 2026 Hardfin, Inc.
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { MovementStatus, pagedMovementSchema } from '@/movement/api/types';
import queryString from 'query-string';
import { z } from 'zod';

const movementsResponseSchema = z
  .object({
    data: pagedMovementSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data }) => ({
    movements: data.results,
    resultCount: data.totalCount,
  }));

type MovementsResponse = z.infer<typeof movementsResponseSchema>;

export interface MovementFilter {
  status?: MovementStatus;
  itemIds?: string[];
  originIds?: string[];
  destinationIds?: string[];
  hasShipment?: string[];
  query?: string;
  pageNumber?: number;
  pageSize?: number;
}

export async function getMovements(
  filter?: MovementFilter
): Promise<MovementsResponse> {
  const url = '/v1/move';
  const response = await api.get(url, {
    paramsSerializer: (parameters) => queryString.stringify(parameters),
    params: {
      status:
        filter?.status && filter.status !== 'all' ? filter.status : undefined,
      itemId: filter?.itemIds,
      originId: filter?.originIds,
      destinationId: filter?.destinationIds,
      hasShipment: materializeShipmentFilter(filter?.hasShipment),
      query: filter?.query,
      page: filter?.pageNumber,
      limit: filter?.pageSize,
    },
  });

  const { movements, resultCount } = await parseResponse(
    movementsResponseSchema,
    response.data,
    {
      schemaName: 'Movements',
      endpoint: url,
    }
  );

  return { movements, resultCount };
}

// Converts checkbox state (['yes'], ['no'], ['yes','no'], or undefined) to a single backend value
function materializeShipmentFilter(
  value: string[] | undefined
): string | undefined {
  if (!value || value.length === 0 || value.length === 2) {
    return undefined;
  }
  return value.includes('yes') ? 'true' : 'false';
}
